import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useFirebase } from '@mesterplan/shared-firebase';

/**
 * Ticket submission, served by the .NET API (`POST /api/support/tickets`).
 *
 * Replaces the `submitGitHubTicket` callable, which opened with
 * `if (!request.auth) throw new HttpsError('unauthenticated', ...)`. This app has
 * carried no Firebase auth session since the RsConnect cutover, so every submission
 * from the web failed — the "Send feedback" dialog could not file a single ticket.
 */

export type TicketType = 'bug' | 'feature' | 'enhancement';

export interface SubmitTicketInput {
    type: TicketType;
    title: string;
    description: string;
    // Bug-specific fields
    stepsToReproduce?: string;
    expectedBehavior?: string;
    actualBehavior?: string;
    // Browser/environment info
    browser?: string;
    device?: string;
}

export interface SubmitTicketResult {
    success: boolean;
    ticketId: string;
    issueNumber: number;
    issueUrl: string;
}

export function useSubmitTicket() {
    const { api } = useFirebase();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (input: SubmitTicketInput): Promise<SubmitTicketResult> => {
            if (!api) throw new Error('Support API not configured');

            const response = await api.support.createTicket({
                type: input.type,
                title: input.title,
                description: input.description,
                stepsToReproduce: input.stepsToReproduce,
                expectedBehavior: input.expectedBehavior,
                actualBehavior: input.actualBehavior,
                browser: input.browser,
                device: input.device,
            });

            if (!response.ok) {
                throw new Error(
                    response.error?.detail || response.error?.title || 'Kunne ikke oprette sagen'
                );
            }

            const ticket = response.data;
            return {
                success: true,
                ticketId: ticket.id,
                issueNumber: ticket.githubIssueNumber,
                issueUrl: ticket.githubIssueUrl,
            };
        },
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: ['MY_TICKETS'] });
        },
    });
}
